"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MerkleTree = void 0;
const tslib_1 = require("tslib");
const js_sha3_1 = require("js-sha3");
const tiny_invariant_1 = (0, tslib_1.__importDefault)(require("tiny-invariant"));
class MerkleTree {
    constructor(elements) {
        this.elements = [...elements];
        // Sort elements
        this.elements.sort(Buffer.compare.bind(null));
        // Deduplicate elements
        this.elements = MerkleTree.bufDedup(this.elements);
        this.bufferElementPositionIndex = this.elements.reduce((memo, el, index) => {
            memo[el.toString("hex")] = index;
            return memo;
        }, {});
        // Create layers
        this.layers = this.getLayers(this.elements);
    }
    getLayers(elements) {
        var _a, _b;
        if (elements.length === 0) {
            throw new Error("empty tree");
        }
        const layers = [];
        layers.push(elements);
        // Get next layer until we reach the root
        while (((_b = (_a = layers[layers.length - 1]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 1) {
            const nextLayerIndex = layers[layers.length - 1];
            (0, tiny_invariant_1.default)(nextLayerIndex, "nextLayerIndex");
            layers.push(this.getNextLayer(nextLayerIndex));
        }
        return layers;
    }
    getNextLayer(elements) {
        return elements.reduce((layer, el, idx, arr) => {
            if (idx % 2 === 0) {
                // Hash the current element with its pair element
                const pairEl = arr[idx + 1];
                layer.push(MerkleTree.combinedHash(el, pairEl));
            }
            return layer;
        }, []);
    }
    static combinedHash(first, second) {
        if (!first) {
            (0, tiny_invariant_1.default)(second, "second element of pair must exist");
            return second;
        }
        if (!second) {
            (0, tiny_invariant_1.default)(first, "first element of pair must exist");
            return first;
        }
        return Buffer.from(js_sha3_1.keccak_256.digest(MerkleTree.sortAndConcat(first, second)));
    }
    getRoot() {
        var _a;
        const root = (_a = this.layers[this.layers.length - 1]) === null || _a === void 0 ? void 0 : _a[0];
        (0, tiny_invariant_1.default)(root, "root");
        return root;
    }
    getHexRoot() {
        return this.getRoot().toString("hex");
    }
    getProof(el) {
        const initialIdx = this.bufferElementPositionIndex[el.toString("hex")];
        if (typeof initialIdx !== "number") {
            throw new Error("Element does not exist in Merkle tree");
        }
        let idx = initialIdx;
        return this.layers.reduce((proof, layer) => {
            const pairElement = MerkleTree.getPairElement(idx, layer);
            if (pairElement) {
                proof.push(pairElement);
            }
            idx = Math.floor(idx / 2);
            return proof;
        }, []);
    }
    getHexProof(el) {
        const proof = this.getProof(el);
        return MerkleTree.bufArrToHexArr(proof);
    }
    static getPairElement(idx, layer) {
        const pairIdx = idx % 2 === 0 ? idx + 1 : idx - 1;
        if (pairIdx < layer.length) {
            const pairEl = layer[pairIdx];
            (0, tiny_invariant_1.default)(pairEl, "pairEl");
            return pairEl;
        }
        else {
            return null;
        }
    }
    static bufDedup(elements) {
        return elements.filter((el, idx) => {
            var _a;
            return idx === 0 || !((_a = elements[idx - 1]) === null || _a === void 0 ? void 0 : _a.equals(el));
        });
    }
    static bufArrToHexArr(arr) {
        if (arr.some((el) => !Buffer.isBuffer(el))) {
            throw new Error("Array is not an array of buffers");
        }
        return arr.map((el) => "0x" + el.toString("hex"));
    }
    static sortAndConcat(...args) {
        return Buffer.concat([...args].sort(Buffer.compare.bind(null)));
    }
}
exports.MerkleTree = MerkleTree;
//# sourceMappingURL=merkle-tree.js.map